import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
  DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Trash2, Upload, Plus, Video as VideoIcon, Calendar, Camera as CameraIcon,
  Download, RefreshCw, Terminal, LogOut, EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { istTimeStr, istISODateStr } from "@/lib/time";
import { QUALITY_PRESETS, compressVideoClientSide, estimateSize, formatBytes } from "@/lib/videoCompress";

const DAYS_LONG = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function todayISO(offset = 0) {
  const d = new Date();
  d.setDate(d.getDate() + offset);
  return istISODateStr(d);
}
function pad(n) { return String(n).padStart(2, "0"); }

export default function OperatorPanel() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cameras, setCameras] = useState([]);
  const [footages, setFootages] = useState([]);
  const [refreshTick, setRefreshTick] = useState(0);
  const [now, setNow] = useState(new Date());
  const refresh = () => setRefreshTick((t) => t + 1);

  // Super-admin can manage on behalf of any tenant. Pick one first.
  const isSuper = user?.role === "super_admin";
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(null);

  useEffect(() => {
    if (!isSuper) return;
    api.get("/tenants").then((r) => {
      setTenants(r.data);
      if (r.data.length) {
        setActiveTenantId((prev) => prev || r.data[0].id);
      }
    }).catch((e) => toast.error(formatApiError(e)));
  }, [isSuper]);

  const tenantParams = isSuper && activeTenantId ? { tenant_id: activeTenantId } : null;

  useEffect(() => {
    // For super_admin, defer loading until a tenant is picked.
    if (isSuper && !activeTenantId) return;
    const cfg = tenantParams ? { params: tenantParams } : {};
    Promise.all([api.get("/cameras", cfg), api.get("/footage", cfg)])
      .then(([c, f]) => { setCameras(c.data); setFootages(f.data); })
      .catch((e) => toast.error(formatApiError(e)));
  }, [refreshTick, activeTenantId, isSuper, tenantParams]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const onLogout = () => { navigate("/nvr-console", { replace: true }); setTimeout(logout, 0); };
  const timeStr = istTimeStr(now);

  return (
    <div className="min-h-screen bg-black text-zinc-100 flex flex-col">
      {/* Amber operator header */}
      <header className="border-b border-amber-500/40 bg-black">
        <div className="bg-amber-500 text-black px-4 h-1" />
        <div className="flex items-center justify-between px-4 h-11">
            <div className="flex items-center gap-3">
              <Terminal size={16} className="text-amber-500" />
              <img src="/logo.png" alt="Cam Control Hub" style={{ height: 26 }} className="object-contain" />
              <span className="mono text-[10px] text-amber-500/80 uppercase tracking-widest border-l border-zinc-800 pl-3">Operator Console</span>
              {user?.tenant?.name && (
                <span className="mono text-[10px] text-zinc-500 border-l border-zinc-800 pl-3">
                  {user.tenant.name}
                </span>
              )}
            </div>
          <div className="flex items-center gap-4">
            <div className="mono text-[11px] text-amber-400">{timeStr} <span className="text-[9px] text-amber-500/70">IST</span></div>
            {user && (
              <div className="flex items-center gap-2 border-l border-zinc-900 pl-4">
                <EyeOff size={13} className="text-amber-500" />
                <span className="mono text-[11px] text-zinc-300">{user.username}</span>
                <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-400">
                  {user.role}
                </span>
                <Button data-testid="op-logout" variant="ghost" onClick={onLogout} className="rounded-none h-7 px-2 text-zinc-400 hover:text-red-400">
                  <LogOut size={13} />
                </Button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="px-4 py-3 border-b border-zinc-900 bg-zinc-950 flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="mono text-[10px] uppercase tracking-widest text-amber-500">RESTRICTED · Video Content Management</div>
          <h1 className="font-display text-xl mt-1">NVR Configuration</h1>
        </div>
        {isSuper && (
          <div className="flex items-center gap-2">
            <span className="mono text-[10px] uppercase tracking-widest text-cyan-400">Managing tenant</span>
            <Select value={activeTenantId || ""} onValueChange={setActiveTenantId}>
              <SelectTrigger data-testid="op-tenant-picker" className="rounded-none bg-black border-cyan-500/40 mono h-8 min-w-[240px] text-[11px]">
                <SelectValue placeholder="Select tenant..." />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-zinc-950 border-cyan-500/40">
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="mono text-[10px] text-zinc-500">
          {cameras.length} cameras · {footages.length} footages
        </div>
      </div>

      <div className="flex-1 p-4 overflow-auto">
        {isSuper && !activeTenantId ? (
          <div className="mono text-[11px] uppercase tracking-widest text-amber-400 border border-amber-500/40 p-4 bg-amber-950/20">
            ▸ Select a tenant above to manage its cameras / footage / day-slots / downloads.
          </div>
        ) : (
          <Tabs defaultValue="cameras" className="w-full">
            <TabsList className="rounded-none bg-zinc-950 border border-zinc-900 p-0 h-9">
              <TabsTrigger data-testid="op-tab-cameras" value="cameras" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Cameras</TabsTrigger>
              <TabsTrigger data-testid="op-tab-footage" value="footage" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Footage</TabsTrigger>
              <TabsTrigger data-testid="op-tab-dayslots" value="dayslots" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Day Slots</TabsTrigger>
              <TabsTrigger data-testid="op-tab-downloads" value="downloads" className="rounded-none px-4 h-9 text-[11px] uppercase tracking-widest data-[state=active]:bg-amber-500/10 data-[state=active]:text-amber-400">Downloads</TabsTrigger>
            </TabsList>

            <TabsContent value="cameras" className="mt-4"><CamerasTab cameras={cameras} footages={footages} onChange={refresh} tenantId={activeTenantId} isSuper={isSuper} /></TabsContent>
            <TabsContent value="footage" className="mt-4"><FootageTab footages={footages} onChange={refresh} tenantId={activeTenantId} isSuper={isSuper} /></TabsContent>
            <TabsContent value="dayslots" className="mt-4"><DaySlotsTab cameras={cameras} footages={footages} /></TabsContent>
            <TabsContent value="downloads" className="mt-4"><DownloadsTab cameras={cameras} tenantId={activeTenantId} isSuper={isSuper} /></TabsContent>
          </Tabs>
        )}
      </div>
    </div>
  );
}

/* -------------------- Cameras Tab -------------------- */
function CamerasTab({ cameras, footages, onChange, tenantId, isSuper }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", location: "", channel: 1, default_footage_id: "none",
    type: "recorded", rtsp_url: "", onvif_username: "", onvif_password: "",
  });

  const submit = async () => {
    if (isSuper && !tenantId) { toast.error("Select a tenant first"); return; }
    try {
      const payload = {
        name: form.name, location: form.location, channel: Number(form.channel),
        default_footage_id: form.default_footage_id === "none" ? null : form.default_footage_id,
        type: form.type,
        ...(tenantId ? { tenant_id: tenantId } : {}),
      };
      if (form.type === "onvif") {
        payload.rtsp_url = form.rtsp_url;
        payload.onvif_username = form.onvif_username;
        if (form.onvif_password) payload.onvif_password = form.onvif_password;
      }
      await api.post("/cameras", payload);
      toast.success("Camera added");
      setOpen(false);
      setForm({
        name: "", location: "", channel: cameras.length + 1, default_footage_id: "none",
        type: "recorded", rtsp_url: "", onvif_username: "", onvif_password: "",
      });
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const del = async (id) => {
    if (!confirm("Delete this camera?")) return;
    await api.delete(`/cameras/${id}`);
    toast.success("Camera deleted");
    onChange();
  };

  const setDefault = async (cam, footage_id) => {
    await api.patch(`/cameras/${cam.id}`, { default_footage_id: footage_id === "none" ? null : footage_id });
    toast.success(`Assigned to ${cam.name}`);
    onChange();
  };

  const onvifCheck = async (cam) => {
    try {
      const { data } = await api.post(`/cameras/${cam.id}/onvif-check`);
      toast.success(data.onvif_online ? `Reachable at ${data.host}:${data.port}` : `Unreachable at ${data.host}:${data.port}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const setDisplayMode = async (cam, mode) => {
    try {
      await api.patch(`/cameras/${cam.id}`, { display_mode: mode });
      toast.success(`${cam.name} → ${mode === "live" ? "LIVE (ONVIF)" : "RECORDED"}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const bulkMode = async (mode) => {
    const affected = cameras.filter((c) => mode === "recorded" || c.type === "onvif");
    if (mode === "live" && affected.length === 0) {
      toast.error("No ONVIF cameras registered — only ONVIF cameras can go LIVE");
      return;
    }
    if (!confirm(`Switch ${affected.length} camera(s) to ${mode.toUpperCase()} mode?`)) return;
    try {
      const { data } = await api.post("/cameras/bulk-display-mode", { display_mode: mode });
      toast.success(`${data.updated} camera(s) → ${mode.toUpperCase()}`);
      onChange();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest">Camera Registry</div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="op-bulk-recorded"
            variant="ghost"
            onClick={() => bulkMode("recorded")}
            className="rounded-none h-8 text-[10px] uppercase tracking-widest border border-cyan-500/40 text-cyan-400 hover:bg-cyan-950/30"
          >
            All → Recorded
          </Button>
          <Button
            data-testid="op-bulk-live"
            variant="ghost"
            onClick={() => bulkMode("live")}
            className="rounded-none h-8 text-[10px] uppercase tracking-widest border border-red-500/40 text-red-400 hover:bg-red-950/30"
          >
            All → Live (ONVIF)
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="op-add-camera-btn" className="rounded-none bg-amber-500 hover:bg-amber-400 text-black h-8 text-[11px] uppercase tracking-widest">
              <Plus size={14} className="mr-1" /> Add Camera
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="font-display">Register Camera</DialogTitle>
              <DialogDescription className="mono text-[10px] text-zinc-500">Add a new camera channel.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Camera Name</Label>
                <Input data-testid="op-cam-name" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Location</Label>
                <Input data-testid="op-cam-location" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Channel Number</Label>
                <Input data-testid="op-cam-channel" type="number" min="1" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.channel} onChange={(e) => setForm({ ...form, channel: e.target.value })} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Default Footage (fallback)</Label>
                <Select value={form.default_footage_id} onValueChange={(v) => setForm({ ...form, default_footage_id: v })}>
                  <SelectTrigger data-testid="op-cam-default-footage" className="rounded-none bg-black border-zinc-800 mono mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                    <SelectItem value="none">— None —</SelectItem>
                    {footages.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                  </SelectContent>
                </Select></div>

              <div className="border-t border-zinc-800 pt-3">
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Camera Type</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="op-cam-type" className="rounded-none bg-black border-zinc-800 mono mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                    <SelectItem value="recorded">Recorded only (virtual camera)</SelectItem>
                    <SelectItem value="onvif">ONVIF / IP camera (real hardware)</SelectItem>
                  </SelectContent>
                </Select>
                <div className="mono text-[10px] text-zinc-600 mt-1">
                  Both types display recorded footage. ONVIF adds real hardware presence + connectivity check.
                </div>
              </div>

              {form.type === "onvif" && (
                <div className="border border-amber-500/30 bg-amber-950/10 p-3 space-y-3">
                  <div className="mono text-[10px] uppercase tracking-widest text-amber-400">ONVIF Camera Details</div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">RTSP URL</Label>
                    <Input data-testid="op-cam-rtsp" placeholder="rtsp://192.168.1.108:554/stream1" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.rtsp_url} onChange={(e) => setForm({ ...form, rtsp_url: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Camera Username</Label>
                      <Input data-testid="op-cam-onvif-user" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.onvif_username} onChange={(e) => setForm({ ...form, onvif_username: e.target.value })} /></div>
                    <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Camera Password</Label>
                      <Input data-testid="op-cam-onvif-pass" type="password" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.onvif_password} onChange={(e) => setForm({ ...form, onvif_password: e.target.value })} /></div>
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="op-cam-save" onClick={submit} disabled={!form.name} className="rounded-none bg-amber-500 hover:bg-amber-400 text-black">Register</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {cameras.length === 0 ? (
        <div className="border border-dashed border-zinc-900 p-8 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">No cameras.</div>
      ) : (
        <div className="border border-zinc-900">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-zinc-950 border-b border-zinc-900 text-zinc-500 text-[10px] uppercase tracking-widest">
                <th className="text-left p-2 font-normal">CH</th>
                <th className="text-left p-2 font-normal">Name</th>
                <th className="text-left p-2 font-normal">Type</th>
                <th className="text-left p-2 font-normal">Display Mode</th>
                <th className="text-left p-2 font-normal">Location</th>
                <th className="text-left p-2 font-normal">Default Footage</th>
                <th className="text-right p-2 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {cameras.map((c) => (
                <tr key={c.id} className="border-b border-zinc-900 hover:bg-zinc-950/50">
                  <td className="p-2 mono text-amber-400">CH{String(c.channel).padStart(2, "0")}</td>
                  <td className="p-2 mono">{c.name}</td>
                  <td className="p-2">
                    {c.type === "onvif" ? (
                      <div className="flex items-center gap-1.5">
                        <span
                          data-testid={`op-cam-onvif-badge-${c.channel}`}
                          className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-400"
                        >ONVIF</span>
                        {c.onvif_online === true && <span className="mono text-[9px] text-emerald-400">● online</span>}
                        {c.onvif_online === false && <span className="mono text-[9px] text-red-400">● offline</span>}
                        {c.onvif_online == null && <span className="mono text-[9px] text-zinc-600">● unchecked</span>}
                      </div>
                    ) : (
                      <span className="mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 text-zinc-400">virtual</span>
                    )}
                  </td>
                  <td className="p-2">
                    {c.type === "onvif" ? (
                      <Select value={c.display_mode || "recorded"} onValueChange={(v) => setDisplayMode(c, v)}>
                        <SelectTrigger data-testid={`op-cam-mode-${c.channel}`} className="rounded-none bg-black border-zinc-800 mono h-7 text-[11px] w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                          <SelectItem value="recorded">Recorded (loop)</SelectItem>
                          <SelectItem value="live">Live (real feed)</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="mono text-[9px] text-zinc-600 uppercase tracking-widest">recorded only</span>
                    )}
                  </td>
                  <td className="p-2 mono text-zinc-500">{c.location || "—"}</td>
                  <td className="p-2">
                    <Select value={c.default_footage_id || "none"} onValueChange={(v) => setDefault(c, v)}>
                      <SelectTrigger data-testid={`op-cam-assign-${c.channel}`} className="rounded-none bg-black border-zinc-800 mono h-7 text-[11px] w-56"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                        <SelectItem value="none">— None —</SelectItem>
                        {footages.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-2 text-right whitespace-nowrap">
                    {c.type === "onvif" && (
                      <Button
                        data-testid={`op-cam-onvif-check-${c.channel}`}
                        variant="ghost"
                        onClick={() => onvifCheck(c)}
                        className="rounded-none h-7 text-amber-400 hover:bg-amber-950/40 mr-1 text-[10px] uppercase tracking-widest"
                      >
                        Check
                      </Button>
                    )}
                    <Button data-testid={`op-cam-delete-${c.channel}`} variant="ghost" onClick={() => del(c.id)} className="rounded-none h-7 text-red-400 hover:bg-red-950/40"><Trash2 size={13} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* -------------------- Footage Tab -------------------- */
const CHUNK_SIZE = 4 * 1024 * 1024; // 4 MB — safely below Cloudflare 100 MB body limit

function FootageTab({ footages, onChange, tenantId, isSuper }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const [progress, setProgress] = useState(0);
  const [chunkStatus, setChunkStatus] = useState(""); // "Chunk 3/56"

  const [quality, setQuality] = useState("original"); // one of QUALITY_PRESETS keys
  const [videoDuration, setVideoDuration] = useState(0);

  // Probe duration when file is chosen so we can show an estimated compressed size
  useEffect(() => {
    if (!file) { setVideoDuration(0); return; }
    const url = URL.createObjectURL(file);
    const v = document.createElement("video");
    v.src = url; v.preload = "metadata";
    v.onloadedmetadata = () => { setVideoDuration(v.duration || 0); URL.revokeObjectURL(url); };
    v.onerror = () => { setVideoDuration(0); URL.revokeObjectURL(url); };
  }, [file]);

  const preset = QUALITY_PRESETS[quality];
  const estSize = preset && videoDuration ? estimateSize(videoDuration, preset) : null;

  const submit = async () => {
    if (!file || !name) return;
    if (isSuper && !tenantId) {
      toast.error("Select a tenant first");
      return;
    }
    setUploading(true);
    setProgress(0);
    let uploadId = null;
    try {
      // 0) Optional client-side compression
      let uploadFile = file;
      let uploadName = file.name;
      let uploadType = file.type || "video/mp4";
      if (preset) {
        setChunkStatus(`Compressing → ${preset.label}`);
        const compressed = await compressVideoClientSide(file, preset, (pct) => setProgress(pct));
        // Reset progress for upload phase
        setProgress(0);
        uploadFile = compressed;
        uploadType = compressed.type || "video/webm";
        // Change ext to .webm since we re-encoded
        const stem = file.name.replace(/\.[^.]+$/, "");
        uploadName = `${stem}.webm`;
        toast.success(`Compressed to ${formatBytes(compressed.size)} (${((compressed.size / file.size) * 100).toFixed(0)}% of source)`);
      }

      setChunkStatus("Preparing...");
      // 1) Init upload session
      const initResp = await api.post("/footage/upload/init", {
        name,
        description,
        content_type: uploadType,
        total_size: uploadFile.size,
        original_filename: uploadName,
        ...(tenantId ? { tenant_id: tenantId } : {}),
      });
      uploadId = initResp.data.upload_id;
      const totalChunks = Math.max(1, Math.ceil(uploadFile.size / CHUNK_SIZE));

      // 2) Upload chunks sequentially (with per-chunk progress mapped to overall %)
      for (let i = 0; i < totalChunks; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(uploadFile.size, start + CHUNK_SIZE);
        const blob = uploadFile.slice(start, end);
        const fd = new FormData();
        fd.append("upload_id", uploadId);
        fd.append("chunk_index", String(i));
        fd.append("total_chunks", String(totalChunks));
        fd.append("chunk", blob, `chunk_${i}`);
        setChunkStatus(`Chunk ${i + 1}/${totalChunks}`);
        await api.post("/footage/upload/chunk", fd, {
          timeout: 5 * 60 * 1000,
          onUploadProgress: (evt) => {
            if (!evt.total) return;
            const chunkFrac = evt.loaded / evt.total;
            const overall = ((i + chunkFrac) / totalChunks) * 100;
            setProgress(Math.min(99, Math.round(overall)));
          },
        });
      }

      // 3) Finalize (server assembles + uploads to storage)
      setChunkStatus("Finalizing...");
      await api.post("/footage/upload/complete", {
        upload_id: uploadId,
        total_chunks: totalChunks,
      });
      setProgress(100);
      toast.success("Footage uploaded");
      setOpen(false); setName(""); setDescription(""); setFile(null); onChange();
    } catch (e) {
      toast.error(formatApiError(e));
      // Best-effort abort — server will GC stale sessions anyway
      if (uploadId) {
        try { await api.delete(`/footage/upload/${uploadId}`); } catch (_) { /* ignore */ }
      }
    } finally {
      setUploading(false); setProgress(0); setChunkStatus("");
    }
  };

  const del = async (id) => {
    if (!confirm("Delete this footage?")) return;
    await api.delete(`/footage/${id}`);
    toast.success("Footage deleted"); onChange();
  };

  const formatSize = (b) => {
    if (!b) return "—";
    const mb = b / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest">Footage Library</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="op-upload-footage-btn" className="rounded-none bg-amber-500 hover:bg-amber-400 text-black h-8 text-[11px] uppercase tracking-widest">
              <Upload size={14} className="mr-1" /> Upload Footage
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
            <DialogHeader>
              <DialogTitle className="font-display">Upload Video Footage</DialogTitle>
              <DialogDescription className="mono text-[10px] text-zinc-500">Upload a video file for use as day-slot content.</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Name</Label>
                <Input data-testid="op-footage-name" className="mono bg-black border-zinc-800 rounded-none mt-1" value={name} onChange={(e) => setName(e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Description (optional)</Label>
                <Textarea data-testid="op-footage-desc" className="mono bg-black border-zinc-800 rounded-none mt-1" value={description} onChange={(e) => setDescription(e.target.value)} /></div>
              <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Video File</Label>
                <Input data-testid="op-footage-file" type="file" accept="video/*" className="mono bg-black border-zinc-800 rounded-none mt-1" onChange={(e) => setFile(e.target.files?.[0] || null)} />
                {file && <div className="mono text-[10px] text-zinc-500 mt-1">{file.name} · {formatSize(file.size)}{videoDuration ? ` · ${Math.round(videoDuration)}s` : ""}</div>}</div>

              {/* Compression preset */}
              <div>
                <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Compress before upload</Label>
                <Select value={quality} onValueChange={setQuality}>
                  <SelectTrigger data-testid="op-footage-quality" className="rounded-none bg-black border-zinc-800 mono h-8 mt-1 text-[11px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                    <SelectItem value="original">Original (no re-encode)</SelectItem>
                    <SelectItem value="fhd">1080p · High (~4 Mbps)</SelectItem>
                    <SelectItem value="hd">720p · Medium (~2 Mbps)</SelectItem>
                    <SelectItem value="sd">480p · Low (~1 Mbps)</SelectItem>
                  </SelectContent>
                </Select>
                {preset && (
                  <div className="mono text-[10px] text-zinc-500 mt-1 flex items-center justify-between">
                    <span>▸ Runs in your browser — takes ~= source duration.</span>
                    {estSize && file && estSize < file.size && (
                      <span className="text-cyan-400">
                        ≈ up to {formatBytes(estSize)} ({((estSize / file.size) * 100).toFixed(0)}%)
                      </span>
                    )}
                  </div>
                )}
                {!preset && file && file.size > 200 * 1024 * 1024 && (
                  <div className="mono text-[10px] text-amber-400 mt-1">
                    ⚠ File is {formatSize(file.size)} — consider compressing for faster upload.
                  </div>
                )}
              </div>

              {uploading && (
                <div className="border border-amber-500/40 bg-amber-950/20 p-2">
                  <div className="flex items-center justify-between mono text-[10px] uppercase tracking-widest">
                    <span className="text-amber-400">{chunkStatus || "Uploading..."}</span>
                    <span className="text-amber-300">{progress}%</span>
                  </div>
                  <div className="mt-1 h-1.5 bg-zinc-800 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-amber-500 transition-all" style={{ width: `${progress}%` }} />
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button data-testid="op-footage-upload-submit" disabled={!name || !file || uploading} onClick={submit} className="rounded-none bg-amber-500 hover:bg-amber-400 text-black">
                {uploading ? "Uploading..." : "Upload"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {footages.length === 0 ? (
        <div className="border border-dashed border-zinc-900 p-8 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">No footage uploaded yet.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {footages.map((f) => (
            <div key={f.id} className="border border-zinc-900 bg-zinc-950 p-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 min-w-0">
                  <VideoIcon size={16} className="text-amber-400 mt-0.5 shrink-0" />
                  <div className="min-w-0">
                    <div className="mono text-[13px] truncate">{f.name}</div>
                    <div className="mono text-[10px] text-zinc-500 truncate">{f.original_filename}</div>
                  </div>
                </div>
                <Button data-testid={`op-footage-delete-${f.id}`} variant="ghost" onClick={() => del(f.id)} className="rounded-none h-7 text-red-400 hover:bg-red-950/40"><Trash2 size={13} /></Button>
              </div>
              {f.description && <div className="mono text-[10px] text-zinc-500 mt-2">{f.description}</div>}
              <div className="mono text-[10px] text-zinc-600 mt-3 flex justify-between">
                <span>{formatSize(f.size)}</span>
                <span>{f.uploaded_at?.split("T")[0]}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- Day Slots Tab -------------------- */
function DaySlotsTab({ cameras, footages }) {
  const [selectedCam, setSelectedCam] = useState("");
  const [slots, setSlots] = useState([null, null, null, null, null, null, null]);

  useEffect(() => {
    if (!selectedCam) return;
    api.get(`/cameras/${selectedCam}/day-slots`).then((r) => setSlots(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [selectedCam]);

  const setSlot = async (dow, footage_id) => {
    try {
      await api.put(`/cameras/${selectedCam}/day-slots`, {
        day_of_week: dow, footage_id: footage_id === "none" ? null : footage_id,
      });
      const r = await api.get(`/cameras/${selectedCam}/day-slots`);
      setSlots(r.data);
      toast.success(`${DAYS_LONG[dow]} slot updated`);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const camera = cameras.find((c) => c.id === selectedCam);
  const todayDow = new Date().getDay();
  const mondayDow = todayDow === 0 ? 6 : todayDow - 1;

  return (
    <div>
      <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest mb-3">7-Day Footage Assignment</div>
      <div className="border border-zinc-900 bg-zinc-950 p-4 mb-4">
        <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Select Camera</Label>
        <Select value={selectedCam} onValueChange={setSelectedCam}>
          <SelectTrigger data-testid="op-dayslot-camera-select" className="rounded-none bg-black border-zinc-800 mono mt-1 w-full md:w-96">
            <SelectValue placeholder="— Choose a camera —" />
          </SelectTrigger>
          <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
            {cameras.map((c) => (<SelectItem key={c.id} value={c.id}>CH{String(c.channel).padStart(2, "0")} · {c.name}</SelectItem>))}
          </SelectContent>
        </Select>
      </div>

      {!selectedCam ? (
        <div className="border border-dashed border-zinc-900 p-12 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">Select a camera to configure its 7-day loop.</div>
      ) : (
        <div>
          <div className="mono text-[10px] text-zinc-500 mb-2">
            {camera && <>Configuring: <span className="text-amber-400">CH{String(camera.channel).padStart(2, "0")}</span> {camera.name}</>}
            <span className="ml-4 text-zinc-600">Today is <span className="text-emerald-400">{DAYS_LONG[mondayDow]}</span></span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-7 gap-2">
            {DAYS_LONG.map((day, dow) => {
              const slot = slots[dow];
              const footage = slot ? footages.find((f) => f.id === slot.footage_id) : null;
              const isToday = dow === mondayDow;
              return (
                <div key={dow} data-testid={`op-dayslot-${dow}`}
                     className={`border p-3 bg-black flex flex-col ${isToday ? "border-amber-500" : "border-zinc-900"}`}>
                  <div className="flex items-center justify-between">
                    <div className={`mono text-[11px] uppercase tracking-widest ${isToday ? "text-amber-400" : "text-zinc-500"}`}>{DAYS_SHORT[dow]}</div>
                    {isToday && <span className="mono text-[9px] text-emerald-400 rec-blink">LIVE</span>}
                  </div>
                  <div className="mono text-[9px] text-zinc-600 mt-0.5">{day}</div>
                  <div className="mt-3 border border-zinc-900 aspect-video flex items-center justify-center relative">
                    {footage ? (
                      <>
                        <VideoIcon size={20} className="text-amber-500/60" />
                        <div className="absolute bottom-1 left-1 right-1 mono text-[9px] text-zinc-300 truncate text-center bg-black/70 px-1">{footage.name}</div>
                      </>
                    ) : (
                      <div className="mono text-[9px] text-zinc-700 uppercase tracking-widest">Empty</div>
                    )}
                  </div>
                  <Select value={slot?.footage_id || "none"} onValueChange={(v) => setSlot(dow, v)}>
                    <SelectTrigger data-testid={`op-dayslot-select-${dow}`} className="rounded-none bg-zinc-950 border-zinc-800 mono h-7 text-[11px] mt-2"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                      <SelectItem value="none">— None —</SelectItem>
                      {footages.map((f) => (<SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- Downloads Tab -------------------- */
function DownloadsTab({ cameras, tenantId }) {
  const [jobs, setJobs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ camera_id: "", start_date: todayISO(-1), end_date: todayISO(0) });
  const [busy, setBusy] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(0);

  useEffect(() => {
    const cfg = tenantId ? { params: { tenant_id: tenantId } } : {};
    api.get("/downloads", cfg).then((r) => setJobs(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [autoRefresh, tenantId]);

  useEffect(() => {
    const hasActive = jobs.some((j) => j.status === "queued" || j.status === "processing");
    if (!hasActive) return;
    const t = setInterval(() => setAutoRefresh((x) => x + 1), 3000);
    return () => clearInterval(t);
  }, [jobs]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/downloads", form);
      toast.success("Download job queued");
      setOpen(false); setAutoRefresh((x) => x + 1);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doDownload = (job) => {
    const token = localStorage.getItem("cctv_token");
    window.location.href = `${API}/downloads/${job.id}/file?auth=${encodeURIComponent(token || "")}`;
  };

  const delJob = async (job) => {
    if (!confirm("Delete this download?")) return;
    try { await api.delete(`/downloads/${job.id}`); toast.success("Deleted"); setAutoRefresh((x) => x + 1); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const formatSize = (b) => {
    if (!b) return "—";
    const mb = b / (1024 * 1024);
    return mb >= 1 ? `${mb.toFixed(1)} MB` : `${(b / 1024).toFixed(0)} KB`;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <div className="mono text-[11px] text-zinc-500 uppercase tracking-widest">Footage Exports · date-tagged</div>
        <div className="flex items-center gap-2">
          <Button data-testid="op-downloads-refresh" variant="ghost" onClick={() => setAutoRefresh((x) => x + 1)} className="rounded-none h-8 border border-zinc-800 text-zinc-400"><RefreshCw size={13} /></Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="op-new-download-btn" disabled={cameras.length === 0} className="rounded-none bg-amber-500 hover:bg-amber-400 text-black h-8 text-[11px] uppercase tracking-widest">
                <Download size={14} className="mr-1" /> New Export
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none bg-zinc-950 border-zinc-800 text-zinc-100">
              <DialogHeader>
                <DialogTitle className="font-display">Prepare Footage Export</DialogTitle>
                <DialogDescription className="mono text-[10px] text-zinc-500">Selected date range&apos;s footage will be exported end-to-end as a concatenated MP4. Max 35 days.</DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Camera</Label>
                  <Select value={form.camera_id} onValueChange={(v) => setForm({ ...form, camera_id: v })}>
                    <SelectTrigger data-testid="op-dl-camera" className="rounded-none bg-black border-zinc-800 mono mt-1"><SelectValue placeholder="Select camera" /></SelectTrigger>
                    <SelectContent className="rounded-none bg-zinc-950 border-zinc-800">
                      {cameras.map((c) => (<SelectItem key={c.id} value={c.id}>CH{String(c.channel).padStart(2, "0")} · {c.name}</SelectItem>))}
                    </SelectContent>
                  </Select></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">Start Date</Label>
                    <Input data-testid="op-dl-start" type="date" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                  <div><Label className="text-[10px] uppercase tracking-widest text-zinc-500">End Date</Label>
                    <Input data-testid="op-dl-end" type="date" className="mono bg-black border-zinc-800 rounded-none mt-1" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-widest text-zinc-500">Quick Presets</Label>
                  <div data-testid="op-dl-presets" className="flex flex-wrap gap-2 mt-1">
                    {[
                      { key: "24h", label: "Last 24h", days: 0 },
                      { key: "7d",  label: "Last 7 Days", days: 6 },
                      { key: "30d", label: "Last 30 Days", days: 29 },
                    ].map((p) => {
                      const start = todayISO(-p.days);
                      const end = todayISO(0);
                      const active = form.start_date === start && form.end_date === end;
                      return (
                        <button
                          key={p.key}
                          type="button"
                          data-testid={`op-dl-preset-${p.key}`}
                          onClick={() => setForm((f) => ({ ...f, start_date: start, end_date: end }))}
                          className={`mono text-[10px] uppercase tracking-widest h-7 px-2.5 border ${active ? "bg-amber-500 text-black border-amber-400" : "border-zinc-800 text-zinc-300 hover:border-amber-500/50 hover:text-amber-300"}`}
                        >
                          {p.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="mono text-[10px] text-zinc-600 border border-zinc-900 p-2">▸ System auto-picks each date&apos;s day-of-week footage from the 7-day slots and burns the requested date onto the exported video.</div>
              </div>
              <DialogFooter>
                <Button data-testid="op-dl-submit" disabled={!form.camera_id || busy} onClick={submit} className="rounded-none bg-amber-500 hover:bg-amber-400 text-black">
                  {busy ? "Queueing..." : "Start Export"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {jobs.length === 0 ? (
        <div className="border border-dashed border-zinc-900 p-8 text-center mono text-[11px] text-zinc-600 uppercase tracking-widest">No export jobs yet.</div>
      ) : (
        <div className="border border-zinc-900">
          <table className="w-full text-[12px]">
            <thead>
              <tr className="bg-zinc-950 border-b border-zinc-900 text-zinc-500 text-[10px] uppercase tracking-widest">
                <th className="text-left p-2 font-normal">Camera</th>
                <th className="text-left p-2 font-normal">Range</th>
                <th className="text-left p-2 font-normal">Status</th>
                <th className="text-left p-2 font-normal">Size</th>
                <th className="text-right p-2 font-normal">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => (
                <tr key={j.id} data-testid={`op-dl-row-${j.id}`} className="border-b border-zinc-900 hover:bg-zinc-950/50">
                  <td className="p-2 mono"><span className="text-amber-400">CH{String(j.camera_channel || 0).padStart(2, "0")}</span> {j.camera_name}</td>
                  <td className="p-2 mono text-[11px]"><Calendar size={11} className="inline mr-1 text-zinc-500" />{j.start_date} → {j.end_date}</td>
                  <td className="p-2"><StatusPill job={j} /></td>
                  <td className="p-2 mono text-[11px] text-zinc-500">{formatSize(j.file_size)}</td>
                  <td className="p-2 text-right">
                    {j.status === "ready" && (
                      <Button data-testid={`op-dl-download-${j.id}`} onClick={() => doDownload(j)} className="rounded-none h-7 bg-emerald-600 hover:bg-emerald-500 text-black mr-1 text-[11px] uppercase tracking-widest">
                        <Download size={13} className="mr-1" /> Download
                      </Button>
                    )}
                    <Button data-testid={`op-dl-delete-${j.id}`} variant="ghost" onClick={() => delJob(j)} className="rounded-none h-7 text-red-400 hover:bg-red-950/40"><Trash2 size={13} /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ job }) {
  const s = job.status;
  if (s === "queued") return <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 text-zinc-400">Queued</span>;
  if (s === "processing")
    return (
      <div className="flex items-center gap-2">
        <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-amber-500/60 text-amber-400 rec-blink">Processing</span>
        <div className="w-24 h-1 bg-zinc-800 relative"><div className="absolute inset-y-0 left-0 bg-amber-500" style={{ width: `${job.progress || 0}%` }} /></div>
        <span className="mono text-[10px] text-zinc-500">{job.progress || 0}%</span>
      </div>
    );
  if (s === "ready") return <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-emerald-500/60 text-emerald-400">Ready</span>;
  return (
    <div className="flex items-center gap-2">
      <span className="mono text-[10px] uppercase tracking-widest px-1.5 py-0.5 border border-red-500/60 text-red-400">Failed</span>
      {job.error && <span className="mono text-[10px] text-red-400 truncate max-w-[240px]" title={job.error}>{job.error}</span>}
    </div>
  );
}
